import { useCallback } from "react";
import { useLocation } from "react-router";

import { useNavigateWithState } from "./useNavigateWithState";

/** Props for a react-router `<Link>` (uses `to` + `state`, not `href`). */
export type ReactRouterLinkProps<
	TState extends object | undefined = undefined,
> = {
	state: TState;
	to: string;
};

/**
 * Props for a react-aria link-style `Button` / `AriaLink` (uses `href` +
 * `routerOptions`, so state flows through react-aria's `RouterProvider`).
 */
export type RouterLinkProps<TState extends object | undefined = undefined> = {
	href: string;
	routerOptions: { state: TState };
};

/**
 * Shared `{ navigate, getLinkProps, getRouterLinkProps }` set that attach the
 * same `location.state`.
 *
 * Feature hooks supply stable module-level `buildTo` / `buildState` helpers.
 * Prefer `getLinkProps` for `Button`/`AriaLink` with `href` so react-aria's
 * `RouterProvider` receives the same `state` (plain `onClick` is bypassed), and
 * `getRouterLinkProps` for a react-router `<Link>` (`to` + `state`).
 */
export function useNavigateWithRouterState<
	TArg,
	TState extends object,
>(config: {
	/** Build the `location.state` for the current `pathname` (or `undefined`). */
	buildState: (pathname: string, arg: TArg) => TState | undefined;
	/** Build the destination path/URL for the given argument. */
	buildTo: (arg: TArg) => string;
}) {
	const { pathname } = useLocation();
	const navigateWithState = useNavigateWithState<TState>();
	const { buildState, buildTo } = config;

	/**
	 * Imperatively navigate to the destination, attaching the built state.
	 * Use for row-click handlers, non-link buttons, or preview callbacks.
	 */
	const navigate = useCallback(
		(arg: TArg) => {
			navigateWithState(buildTo(arg), buildState(pathname, arg));
		},
		[buildState, buildTo, navigateWithState, pathname],
	);

	/**
	 * Build `{ href, routerOptions: { state } }` for a react-aria link-style
	 * `Button` / `AriaLink`. Prefer this over `onClick` + `navigate` for `href`
	 * controls, otherwise react-aria's `RouterProvider` drops the state.
	 */
	const getLinkProps = useCallback(
		(arg: TArg): RouterLinkProps<TState | undefined> => ({
			href: buildTo(arg),
			routerOptions: { state: buildState(pathname, arg) },
		}),
		[buildState, buildTo, pathname],
	);

	/**
	 * Build `{ to, state }` to spread onto a react-router `<Link>`, so the link
	 * carries the same `location.state` without hand-rolling it at the call site.
	 */
	const getRouterLinkProps = useCallback(
		(arg: TArg): ReactRouterLinkProps<TState | undefined> => ({
			state: buildState(pathname, arg),
			to: buildTo(arg),
		}),
		[buildState, buildTo, pathname],
	);

	return { getLinkProps, getRouterLinkProps, navigate };
}
